import { AbilityBuilder, Ability, AbilityClass } from '@casl/ability'

const roles = ['admin', 'exporter-admin'] as const

type Role = (typeof roles)[number] | undefined
type Actions = 'manage' | 'read' | 'update' | 'delete' | 'export'
type Subjects = 'OrderList' | 'All'

export type AppAbilityProps = Ability<[Actions, Subjects]>
export const AppAbility = Ability as AbilityClass<AppAbilityProps>

/**
 * Static rule definitions for all user roles
 * @param role 'admin' | 'exporter-admin' | ''
 * @returns The rules for the provided user role
 */

export function defineRulesFor(role: Role) {
  const { can, rules } = new AbilityBuilder(AppAbility)
  const userRole = role ?? ''

  switch (userRole) {
    case 'exporter-admin':
      can('export', 'OrderList')
      can('read', 'All')
      break
    case 'admin':
      can('read', 'All')
      break
    default:
      break
  }

  return rules
}

/**
 * Builds a new Ability instance
 * @param role 'admin' | 'exporter-admin' | ''
 * @returns The Ability instance for the provided user role
 */

export function buildAbiltyFor(role: Role): AppAbilityProps {
  return new AppAbility(defineRulesFor(role))
}

/**
 * Type guard for user roles
 * @param role 'admin' | 'exporter-admin' | ''
 * @returns True if the provided role is a valid user role
 */

export function isRole(role: unknown): role is Role {
  return roles.includes(role as any) || role === undefined
}

/**
 * Sets the user role and builds a new Ability instance
 * @param role 'admin' | 'exporter-admin' | ''
 * @returns  The Ability instance for the provided user role
 */

export function setAbility(role: string): AppAbilityProps {
  if (isRole(role)) return buildAbiltyFor(role)
  return buildAbiltyFor(undefined)
}
